import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import CheckListItem from './ChecklistItem';
import DateInput from '../../components/DateInput';
import CircleImage from '../../components/CircleImage';
import GlobalModal from '../../components/GlobalModal';
import AddRecipientsModal from './AddRecipientsModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { SafetyChecklists } from './SafetyChecklistsSubmit';
import { SafetyChecklistEditChecklistItem } from '../../api/models';
import { SafetyChecklistConditionEditRecipientSummary } from '../../api/models';
import { SafetyChecklistConditionSearchUserSearchResult } from '../../api/models';
import { DomainChecklistAssignmentType } from '../../api/models';
import { GlobalLookupsDomainChecklistFrequency } from '../../api/models';

interface StepOneTypes {
	safetyChecklistsFormData: SafetyChecklists;
	setSafetyChecklistsFormData: React.Dispatch<React.SetStateAction<SafetyChecklists>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	renderedChecklistItems: SafetyChecklistEditChecklistItem[];
	setRenderedChecklistItems: React.Dispatch<React.SetStateAction<SafetyChecklistEditChecklistItem[]>>;
}

const StepOne: React.FC<StepOneTypes> = ({
	safetyChecklistsFormData,
	setSafetyChecklistsFormData,
	setActiveStep,
	renderedChecklistItems,
	setRenderedChecklistItems
}) => {
	const checklistFrequencies = useAppSelector((state) => state.lists.checklistFrequencies) || [];

	const [showAddRecipientsModal, setShowAddRecipientsModal] = React.useState<boolean>(false);
	const [availableRecipients, setAvailableRecipients] = React.useState<
		SafetyChecklistConditionSearchUserSearchResult[]
	>([]);
	const [loadingAvailableRecipients, setLoadingAvailableRecipients] = React.useState<boolean>(false);
	const [availableRecipientsError, setAvailableRecipientsError] = React.useState<string | null>(null);

	const addRecipientHandler = (item: { id?: string; name?: string; profileImage?: string; role?: string }) => {
		if (!item?.id) return;
		const existingIds = safetyChecklistsFormData.recipientUserIds || [];
		if (existingIds.includes(item.id)) {
			return;
		}
		const recipient: SafetyChecklistConditionEditRecipientSummary = {
			id: item.id,
			name: item.name,
			profileImage: item.profileImage,
			role: item.role
		};
		setSafetyChecklistsFormData((prev) => ({
			...prev,
			recipientUserIds: [...(prev.recipientUserIds || []), item.id!],
			recipients: [...(prev.recipients || []), recipient]
		}));
	};

	const removeRecipientHandler = (id: string) => {
		setSafetyChecklistsFormData((prev) => ({
			...prev,
			recipientUserIds: (prev.recipientUserIds || []).filter((uid) => uid !== id),
			recipients: (prev.recipients || []).filter((recipient) => recipient.id !== id)
		}));
	};

	const openAddRecipientsModal = async () => {
		setShowAddRecipientsModal(true);
		setLoadingAvailableRecipients(true);
		setAvailableRecipientsError(null);
		setAvailableRecipients([]);

		const response = await apibridge.getSafetyChecklistConditionAvailableRecipients();
		setLoadingAvailableRecipients(false);

		if (response.isError) {
			setAvailableRecipientsError(
				response.validationErrors?.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'Unable to load recipients'
			);
			setAvailableRecipients([]);
		} else {
			setAvailableRecipients(response.result ?? []);
		}
	};

	const validationSchemaStepOne = Yup.object({
		titleInput: Yup.string().required('Required'),
		frequencyInput: Yup.string().required('Required'),
		startDateInput: Yup.string().required('Required'),
		endDateInput: Yup.string().test('end-after-start', 'End date must be on or after the start date', function (value) {
			const { startDateInput } = this.parent as { startDateInput?: string };
			if (!value?.trim()) return true;
			if (!startDateInput?.trim()) return true;
			const endDay = value.slice(0, 10);
			const startDay = startDateInput.slice(0, 10);
			return endDay >= startDay;
		}),
		assignmentTypeInput: Yup.string().required('Required')
	});

	const validateStepOne = (values: Record<string, unknown>) => {
		const errors: Record<string, string> = {};
		try {
			validationSchemaStepOne.validateSync(values, { abortEarly: false });
		} catch (err) {
			const yupErr = err as { inner?: Array<{ path?: string; message: string }> };
			if (yupErr?.inner) {
				yupErr.inner.forEach((e) => {
					if (e.path) errors[e.path] = e.message;
				});
			}
		}
		if (!safetyChecklistsFormData.recipientUserIds?.length) {
			errors.recipients = 'At least one recipient is required';
		}
		if (!renderedChecklistItems?.length) {
			errors.checklistItems = 'At least one checklist item is required';
		} else if (renderedChecklistItems.some((item) => !item.text?.trim())) {
			errors.checklistItems = 'All checklist items must have text';
		}
		return errors;
	};

	const initialValuesStepOne = {
		titleInput: safetyChecklistsFormData.title || '',
		frequencyInput: safetyChecklistsFormData.frequency || '',
		startDateInput: safetyChecklistsFormData.startDate || '',
		endDateInput: safetyChecklistsFormData.endDate || '',
		assignmentTypeInput: safetyChecklistsFormData.assignmentType || '',
		saveAsTemplateInput: safetyChecklistsFormData.saveAsTemplate ?? false
	};

	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;
		const items = Array.from(renderedChecklistItems);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		setRenderedChecklistItems(items);
	};

	const onSubmitStepOne = (values: {
		titleInput: string;
		frequencyInput: string;
		startDateInput: string;
		endDateInput: string;
		assignmentTypeInput: string;
		saveAsTemplateInput: boolean;
	}) => {
		setSafetyChecklistsFormData((prevState) => ({
			...prevState,
			title: values.titleInput,
			frequency: values.frequencyInput as SafetyChecklists['frequency'],
			startDate: values.startDateInput,
			endDate: values.frequencyInput === 'OneOff' ? values.startDateInput : values.endDateInput,
			assignmentType: values.assignmentTypeInput as DomainChecklistAssignmentType,
			saveAsTemplate: values.saveAsTemplateInput,
			checklistItems: [...renderedChecklistItems]
		}));
		setActiveStep('step2');
	};

	const addChecklistHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();
		setRenderedChecklistItems([...renderedChecklistItems, { text: undefined, id: uuid() }]);
	};

	const removeHandler = (itemIndex: number) => {
		setRenderedChecklistItems(renderedChecklistItems.filter((_, index) => index !== itemIndex));
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validate={validateStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
			validateOnMount={false}
		>
			{(formik) => (
				<>
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main w-100">
								{/* Schedule - Frequency radio buttons */}
								<div className="form-group">
									<label className="body-large mbtom-30">Schedule</label>

									<div className="form-field">
										{checklistFrequencies.map((opt: GlobalLookupsDomainChecklistFrequency) => (
											<div key={opt.value || opt.label} className="general-radio-row align-items-center mbtom-16">
												<input
													id={`frequency_${opt.value}`}
													type="radio"
													name="frequencyInput"
													value={opt.value}
													checked={formik.values.frequencyInput === opt.value}
													onChange={() => formik.setFieldValue('frequencyInput', opt.value)}
												/>
												<div className="option-info align-items-center">
													<label htmlFor={`frequency_${opt.value}`} className="mb-0 mt-0">
														{opt.label}
													</label>
												</div>
											</div>
										))}
										<ErrorMessage name="frequencyInput" component="div" className="error-message" />
									</div>
								</div>

								{/* Start Date */}
								<div className="form-group">
									<label className="label">Start Date</label>
									<div className="w-100-424">
										<DateInput
											dateTimeString={formik.values.startDateInput || ''}
											onChange={(val: string) => {
												formik.setFieldValue('startDateInput', val);
												if (formik.values.frequencyInput === 'OneOff') {
													formik.setFieldValue('endDateInput', val);
												}
												setTimeout(() => formik.validateForm(), 0);
											}}
											excludePast={true}
										/>
									</div>
									<ErrorMessage name="startDateInput" component="div" className="error-message" />
								</div>

								{/* End Date */}
								<div className="form-group">
									<label className="label">End Date</label>
									<div
										className={`w-100-424 ${formik.values.frequencyInput === 'OneOff' ? 'date-input-disabled' : ''}`}
									>
										<DateInput
											dateTimeString={
												formik.values.frequencyInput === 'OneOff'
													? formik.values.startDateInput || ''
													: formik.values.endDateInput || ''
											}
											onChange={
												formik.values.frequencyInput === 'OneOff'
													? undefined
													: (val: string) => {
															formik.setFieldValue('endDateInput', val);
															setTimeout(() => formik.validateForm(), 0);
														}
											}
											excludePast={true}
											disabled={formik.values.frequencyInput === 'OneOff'}
										/>
									</div>
									<ErrorMessage name="endDateInput" component="div" className="error-message" />
								</div>

								<div className="general-divider mbtom-30"></div>

								{/* Assignment and Approval */}
								<div className="form-group">
									<label className="body-large mbtom-30">Assignment and completion</label>

									<div className="form-field">
										<div className="general-radio-row mbtom-24">
											<input
												id="assignmentType_Individual"
												type="radio"
												name="assignmentTypeInput"
												value="Individual"
												checked={formik.values.assignmentTypeInput === 'Individual'}
												onChange={() => formik.setFieldValue('assignmentTypeInput', 'Individual')}
											/>
											<div className="option-info">
												<label htmlFor="assignmentType_Individual">Individual recipients</label>

												<p className="info-sub">
													Assign to singular or multiple users. Each recipient is required to complete the checklist.
												</p>
											</div>
										</div>

										<div className="general-radio-row mbtom-24">
											<input
												id="assignmentType_Group"
												type="radio"
												name="assignmentTypeInput"
												value="Group"
												checked={formik.values.assignmentTypeInput === 'Group'}
												onChange={() => formik.setFieldValue('assignmentTypeInput', 'Group')}
											/>
											<div className="option-info">
												<label htmlFor="assignmentType_Group">Group recipients</label>

												<p className="info-sub">
													Assigned to multiple users, only one recipient from the group is required to complete the
													checklist.
												</p>
											</div>
										</div>
										<ErrorMessage name="assignmentTypeInput" component="div" className="error-message" />
									</div>
								</div>

								{/* Add recipients */}
								<div className="form-group">
									<Button
										type="button"
										className="btn-primary w-auto"
										onClick={() => {
											openAddRecipientsModal();
										}}
									>
										Add recipients
									</Button>
								</div>

								{/* List of recipients */}
								<div className="form-group">
									<h3 className="body-medium fw-bold mtop-30 mbtom-20">List of recipients</h3>
									{formik.submitCount > 0 && (formik.errors as Record<string, string>).recipients && (
										<div className="error-message mbtom-16">{(formik.errors as Record<string, string>).recipients}</div>
									)}

									<div className="general-divider mbtom-16"></div>

									{safetyChecklistsFormData.recipients && safetyChecklistsFormData.recipients.length > 0 ? (
										<section className="info-section incident-form mtop-0 bg-white rounded border-grey-3 p-3 max-height-550 w-100-424">
											<ul>
												{safetyChecklistsFormData.recipients.map((item, index) => (
													<li key={item.id} className="row-container no-hover">
														<div
															className={`row detail-row ${
																index === safetyChecklistsFormData.recipients!.length - 1 ? 'border-bottom-0' : ''
															}`}
														>
															<div className="col-2 pdl-0">
																<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
															</div>
															<div className="col-8 pdl-0">
																<div className="row detail-info center">
																	<div className="col-12 detail-name">
																		<p className="detail-name mbtom-0">{item.name}</p>
																		<p className="label mbtom-0">{item.role}</p>
																	</div>
																</div>
															</div>
															<div className="col-2 link-group pdr-0">
																<Button
																	className="btn-text pdr-0 mtop-0 recipient-list-toggle"
																	onClick={() => {
																		if (item.id) {
																			removeRecipientHandler(item.id);
																			setTimeout(() => formik.validateForm(), 0);
																		}
																	}}
																>
																	Remove
																</Button>
															</div>
														</div>
													</li>
												))}
											</ul>
										</section>
									) : (
										<p className="body-medium text-grey-4">None added</p>
									)}

									<div className="general-divider mtop-16"></div>
								</div>

								<h4 className="body-large mbtom-30">Checklist</h4>

								{/* Title */}
								<div className="form-group">
									<label htmlFor="titleInput" className="label">
										Title
									</label>
									<div className={`form-field ${formik.errors.titleInput ? 'mb-0' : ''}`}>
										<Field name="titleInput" id="titleInput" type="text" className="form-input" />
									</div>
									<ErrorMessage name="titleInput" component="div" className="error-message" />
								</div>

								{/* Checklist items */}
								<div className="">
									<label className="label d-block">Checklist items</label>
									{formik.submitCount > 0 && (formik.errors as Record<string, string>).checklistItems && (
										<div className="error-message mbtom-16">
											{(formik.errors as Record<string, string>).checklistItems}
										</div>
									)}
									{renderedChecklistItems && renderedChecklistItems.length > 0 && (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="checklistItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{renderedChecklistItems.map((item, index) => (
															<Draggable
																key={item.id || index}
																draggableId={(item.id || index).toString()}
																index={index}
															>
																{(provided) => (
																	<li
																		className="mbtom-16"
																		ref={provided.innerRef}
																		{...provided.draggableProps}
																		{...provided.dragHandleProps}
																	>
																		<CheckListItem
																			item={item}
																			index={index}
																			renderedChecklistItems={renderedChecklistItems}
																			setRenderedChecklistItems={setRenderedChecklistItems}
																			removeHandler={(idx) => {
																				removeHandler(idx);
																				setTimeout(() => formik.validateForm(), 0);
																			}}
																			onItemsChange={() => {
																				setTimeout(() => formik.validateForm(), 0);
																			}}
																		/>
																	</li>
																)}
															</Draggable>
														))}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									)}
									<Button
										className="btn-text-icon mbtom-40"
										buttonIcon={<PlusIcon className="link-icon" />}
										onClick={(e) => {
											addChecklistHandler(e);
											setTimeout(() => formik.validateForm(), 0);
										}}
									>
										Add another item
									</Button>
								</div>

								<div className="general-divider"></div>

								{/* Save as template checkbox */}
								<div className="form-group mtop-30">
									<div className="general-radio-row">
										<input
											id="saveAsTemplateInput"
											type="checkbox"
											checked={formik.values.saveAsTemplateInput}
											onChange={(e) => formik.setFieldValue('saveAsTemplateInput', e.target.checked)}
										/>
										<div className="option-info">
											<label htmlFor="saveAsTemplateInput">Save as an organisational checklist template</label>
											<p className="info-sub">
												Are you sure you wish to save this as a template? Organisational checklist templates will be
												available for all users. <br />
												Please note, only the checklist title and items will be saved. The schedule, assignment and
												recipient details will not be saved.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="general-divider mt-0"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>
					</Form>

					<GlobalModal isOpen={showAddRecipientsModal} setIsOpen={setShowAddRecipientsModal} titleText="Add recipients">
						<AddRecipientsModal
							availableRecipients={availableRecipients}
							loading={loadingAvailableRecipients}
							loadError={availableRecipientsError}
							recipientUserIds={safetyChecklistsFormData.recipientUserIds || []}
							onAdd={(item) => {
								addRecipientHandler(item);
								setTimeout(() => formik.validateForm(), 0);
							}}
							onRemove={(id) => {
								removeRecipientHandler(id);
								setTimeout(() => formik.validateForm(), 0);
							}}
						/>
					</GlobalModal>
				</>
			)}
		</Formik>
	);
};

export default StepOne;
